import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { sessionToAuthTokens, type SessionResponse } from '@zolotalk/api-client';
import { apiClient, AUTH_UNAUTHORIZED_EVENT } from '../api/client';
import {
  authenticateWithPasskey,
  isPasskeyAvailable,
  isPasskeyRegistrationAvailable,
  registerWithPasskey,
} from './webauthn';

type AuthStatus = 'loading' | 'authenticated' | 'anonymous';

interface ChallengeResponse {
  challengeId: string;
  options: Record<string, unknown>;
}

interface AuthContextValue {
  status: AuthStatus;
  passkeyAvailable: boolean;
  passkeyRegistrationAvailable: boolean;
  requestMagicLink(email: string): Promise<void>;
  verifyMagicLink(token: string): Promise<void>;
  signInWithPasskey(email?: string): Promise<void>;
  registerPasskey(): Promise<void>;
  signOut(): Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

function hasValidMagicLinkFragment(): boolean {
  if (window.location.pathname !== '/auth/verify') return false;
  const token = new URLSearchParams(window.location.hash.slice(1)).get('token');
  return token !== null && token.length >= 16 && token.length <= 512;
}

async function postPublic<T>(path: string, body: unknown): Promise<T> {
  let response: Response;
  try {
    response = await fetch(path, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
  } catch {
    throw new Error('No connection. Check your network and try again.');
  }

  if (!response.ok) {
    if (response.status === 429) {
      throw new Error('Too many attempts. Please wait a moment and try again.');
    }
    throw new Error('Sign-in could not be completed. Please try again.');
  }
  return response.json() as Promise<T>;
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<AuthStatus>('loading');
  const [magicLinkVerification] = useState(hasValidMagicLinkFragment);

  useEffect(() => {
    let mounted = true;
    const markAnonymous = () => {
      if (mounted) setStatus('anonymous');
    };
    window.addEventListener(AUTH_UNAUTHORIZED_EVENT, markAnonymous);

    if (!magicLinkVerification) {
      void apiClient.getValidAccessToken().then(token => {
        if (mounted) setStatus(token ? 'authenticated' : 'anonymous');
      });
    }

    return () => {
      mounted = false;
      window.removeEventListener(AUTH_UNAUTHORIZED_EVENT, markAnonymous);
    };
  }, [magicLinkVerification]);

  const establishSession = useCallback(async (session: SessionResponse) => {
    await apiClient.setTokens(sessionToAuthTokens(session));
    setStatus('authenticated');
  }, []);

  const requestMagicLink = useCallback(async (email: string) => {
    await postPublic<{ status: 'accepted' }>('/api/auth/magic-link/request', { email });
  }, []);

  const verifyMagicLink = useCallback(
    async (token: string) => {
      if (token.length < 16 || token.length > 512) {
        throw new Error('Sign-in could not be completed. Please try again.');
      }
      await apiClient.clearTokens();
      try {
        const session = await postPublic<SessionResponse>('/api/auth/magic-link/verify', { token });
        await establishSession(session);
      } catch (error) {
        await apiClient.clearTokens();
        setStatus('anonymous');
        throw error;
      }
    },
    [establishSession]
  );

  const signInWithPasskey = useCallback(
    async (email?: string) => {
      const challenge = await postPublic<ChallengeResponse>(
        '/api/auth/passkey/login/options',
        email ? { email } : {}
      );
      const credential = await authenticateWithPasskey(challenge.options);
      const session = await postPublic<SessionResponse>('/api/auth/passkey/login/verify', {
        challengeId: challenge.challengeId,
        credential,
      });
      await establishSession(session);
    },
    [establishSession]
  );

  const registerPasskey = useCallback(async () => {
    const challenge = await apiClient.post<ChallengeResponse>(
      '/api/auth/passkey/register/options',
      {}
    );
    const credential = await registerWithPasskey(challenge.options);
    await apiClient.post<{ credentialId: string }>('/api/auth/passkey/register/verify', {
      challengeId: challenge.challengeId,
      credential,
    });
  }, []);

  const signOut = useCallback(async () => {
    await apiClient.revokeCurrentSession();
    setStatus('anonymous');
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({
      status,
      passkeyAvailable: isPasskeyAvailable(),
      passkeyRegistrationAvailable: isPasskeyRegistrationAvailable(),
      requestMagicLink,
      verifyMagicLink,
      signInWithPasskey,
      registerPasskey,
      signOut,
    }),
    [registerPasskey, requestMagicLink, signInWithPasskey, signOut, status, verifyMagicLink]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

// eslint-disable-next-line react-refresh/only-export-components
export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used inside AuthProvider');
  }
  return context;
}
